import React, { useState, useEffect, useCallback } from 'react';
import {
    Calendar,
    Badge,
    Modal,
    Tag,
    Typography,
    Card,
    Spin,
    message,
    Row,
    Col,
    Statistic,
    Button,
    Space,
    Tabs,
    Form,
    Input,
    Select,
    DatePicker,
    TimePicker,
    Popconfirm,
    Switch,
    Tooltip,
    Empty,
    Alert,
    Checkbox
} from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    UserOutlined,
    CheckCircleOutlined,
    EyeOutlined,
    PlusOutlined,
    StopOutlined,
    DeleteOutlined,
    ReloadOutlined,
    AppstoreOutlined,
    UnorderedListOutlined
} from '@ant-design/icons';
import { bookingsDB, timeSlotsDB, servicesDB, calendarConfigDB } from '../../services/firebase/database';
import moment from 'moment';
import WeekSlotGrid from './WeekSlotGrid';
import './AdminCalendarView.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const AdminCalendarView = ({ isDark }) => {
    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [bookingDetailVisible, setBookingDetailVisible] = useState(false);
    const [selectedDate, setSelectedDate] = useState(moment());
    const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'
    const [dayModalVisible, setDayModalVisible] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [slots, setSlots] = useState([]);
    const [services, setServices] = useState([]);

    // Manual Booking State
    const [manualBookingVisible, setManualBookingVisible] = useState(false);
    const [bookingForm] = Form.useForm();

    // Dynamic Slot Configuration State
    const [addSlotModalVisible, setAddSlotModalVisible] = useState(false);
    const [newSlotTime, setNewSlotTime] = useState(moment('09:00', 'HH:mm'));

    // Custom Slot Creation State
    const [createSlotsModalVisible, setCreateSlotsModalVisible] = useState(false);
    const [createStartTime, setCreateStartTime] = useState(moment('09:00', 'HH:mm'));
    const [createEndTime, setCreateEndTime] = useState(moment('17:00', 'HH:mm'));
    const [slotDuration, setSlotDuration] = useState(30);
    const [preserveSlots, setPreserveSlots] = useState(false);

    const [stats, setStats] = useState({
        monthTotal: 0,
        monthConfirmed: 0,
        monthPending: 0
    });

    // ... (existing helper functions) ...

    const handleCreateSlots = async () => {
        if (!createStartTime || !createEndTime) {
            message.error("Please select both start and end times.");
            return;
        }

        if (createStartTime.isAfter(createEndTime)) {
            message.error("Start time must be before end time.");
            return;
        }

        setActionLoading(true);
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const dayName = selectedDate.format('dddd').toLowerCase();

            // 1. Fetch and Clear existing non-booked slots IF preserveSlots is FALSE
            if (!preserveSlots) {
                const existingSlots = await timeSlotsDB.getSlotsByDate(dateStr);
                const slotsToDelete = existingSlots.filter(s => s.status !== 'booked');

                if (slotsToDelete.length > 0) {
                    const deletePromises = slotsToDelete.map(slot =>
                        timeSlotsDB.deleteSlot(slot.id || `${slot.date}_${slot.time.replace(':', '')}`)
                    );
                    await Promise.all(deletePromises);
                }
            }

            // 2. Construct a temporary businessHours config for just this action
            const customConfig = {
                [dayName]: {
                    enabled: true,
                    start: createStartTime.format('HH:mm'),
                    end: createEndTime.format('HH:mm')
                }
            };

            // 3. Generate new slots
            const newSlots = await timeSlotsDB.generateSlotsForDate(
                dateStr,
                customConfig,
                slotDuration
            );

            if (!preserveSlots) {
                message.success(`Replaced slots. Generated ${newSlots.length} new slots for ${dateStr}.`);
            } else {
                message.success(`Added new slots, preserving existing. Generated ${newSlots.length} slots for ${dateStr}.`);
            }

            setCreateSlotsModalVisible(false);
            fetchSlotsForDay(selectedDate);

        } catch (error) {
            console.error("Error creating slots:", error);
            message.error("Failed to create slots");
        } finally {
            setActionLoading(false);
        }
    };

    const handleSelectDay = (value) => {
        setSelectedDate(value);
        setDayModalVisible(true);
        fetchSlotsForDay(value);
    };

    const handleToggleSlot = async (slot) => {
        setActionLoading(true);
        try {
            const newStatus = slot.status === 'available' ? 'blocked' : 'available';
            if (slot.status === 'booked') {
                message.warning("Cannot block a booked slot. Cancel the booking first.");
                return;
            }

            await timeSlotsDB.upsertSlot({
                ...slot,
                status: newStatus,
                updatedAt: new Date()
            });

            message.success(`Slot ${slot.time} is now ${newStatus}`);
            fetchSlotsForDay(selectedDate);
        } catch (error) {
            console.error('Error toggling slot:', error);
            message.error('Failed to update slot status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDisableAllSlots = async (status = 'blocked') => {
        setActionLoading(true);
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            if (slots.length === 0) {
                message.info('No slots found for this day.');
                return;
            }

            const slotsToUpdate = slots.filter(s => s.status !== 'booked');

            const promises = slotsToUpdate.map(slot =>
                timeSlotsDB.upsertSlot({
                    ...slot,
                    status: status,
                    updatedAt: new Date()
                })
            );

            await Promise.all(promises);
            message.success(`All available slots for ${dateStr} have been ${status === 'blocked' ? 'disabled' : 'enabled'}.`);
            fetchSlotsForDay(selectedDate);
        } catch (error) {
            console.error('Error managing slots:', error);
            message.error('Failed to update slots');
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddSlot = async () => {
        if (!newSlotTime) return;

        setActionLoading(true);
        try {
            const timeStr = newSlotTime.format('HH:mm');
            const dateStr = selectedDate.format('YYYY-MM-DD');

            const exists = slots.some(s => s.time === timeStr);
            if (exists) {
                message.warning('This time slot already exists.');
                return;
            }

            await timeSlotsDB.upsertSlot({
                date: dateStr,
                time: timeStr,
                status: 'available',
                createdAt: new Date()
            });

            message.success(`Added slot at ${timeStr}`);
            setAddSlotModalVisible(false);
            fetchSlotsForDay(selectedDate);
        } catch (error) {
            console.error("Error adding slot:", error);
            message.error("Failed to add slot");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteSlot = async (slot) => {
        if (slot.status === 'booked') {
            message.error("Cannot delete a booked slot.");
            return;
        }

        try {
            const slotId = slot.id || `${slot.date}_${slot.time.replace(':', '')}`;
            await timeSlotsDB.deleteSlot(slotId);

            message.success(`Deleted slot ${slot.time}`);
            setSlots(slots.filter(s => s.time !== slot.time));
        } catch (error) {
            console.error("Error deleting slot:", error);
            message.error("Failed to delete slot");
            fetchSlotsForDay(selectedDate);
        }
    };

    const loadBookings = useCallback(async () => {
        setLoading(true);
        try {
            const allBookings = await bookingsDB.getAll();
            setBookings(allBookings);
            calculateStats(allBookings);

            // Load services for dropdown
            const allServices = await servicesDB.getAll();
            setServices(allServices);
        } catch (error) {
            console.error('Error loading bookings:', error);
            message.error('Failed to load bookings for calendar');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBookings();
    }, [loadBookings]);

    // Fetch Slots for a specific date
    const fetchSlotsForDay = async (date) => {
        try {
            const dateStr = date.format('YYYY-MM-DD');
            let daySlots = await timeSlotsDB.getSlotsByDate(dateStr);

            // If no slots exist, try to generate them from default config
            if (daySlots.length === 0) {
                const config = await calendarConfigDB.getConfig();
                if (config && config.businessHours) {
                    daySlots = await timeSlotsDB.generateSlotsForDate(dateStr, config.businessHours);
                }
            }

            setSlots(daySlots);
        } catch (error) {
            console.error('Error fetching slots:', error);
            // Don't show error to user if it's just empty
        }
    };

    useEffect(() => {
        if (dayModalVisible || manualBookingVisible) {
            fetchSlotsForDay(selectedDate);
        }
    }, [dayModalVisible, manualBookingVisible, selectedDate]);

    const calculateStats = (data) => {
        const now = moment();
        const currentMonthBookings = data.filter(b => {
            const bDate = moment(b.preferredDate?.toDate ? b.preferredDate.toDate() : b.preferredDate);
            return bDate.isSame(now, 'month');
        });

        setStats({
            monthTotal: currentMonthBookings.length,
            monthConfirmed: currentMonthBookings.filter(b => b.status === 'confirmed').length,
            monthPending: currentMonthBookings.filter(b => b.status === 'payment_approved' || b.status === 'pending').length
        });
    };

    const getListData = (value) => {
        const dateStr = value.format('YYYY-MM-DD');
        return bookings.filter(b => {
            const bDate = moment(b.preferredDate?.toDate ? b.preferredDate.toDate() : b.preferredDate).format('YYYY-MM-DD');
            return bDate === dateStr;
        });
    };

    const getStatusType = (status) => {
        switch (status) {
            case 'confirmed': return 'success';
            case 'payment_approved': return 'warning';
            case 'pending': return 'processing';
            case 'cancelled': return 'error';
            case 'completed': return 'default';
            default: return 'processing';
        }
    };

    const dateCellRender = (value) => {
        const listData = getListData(value);
        if (listData.length === 0) return null;

        return (
            <div className="calendar-cell-content">
                <div className="booking-summary-badge">
                    <Badge
                        count={listData.length}
                        style={{ backgroundColor: '#1890ff' }}
                        overflowCount={99}
                    />
                    <span className="booking-count-text">
                        {listData.length === 1 ? 'Booking' : 'Bookings'}
                    </span>
                </div>
                <div className="booking-dots-preview">
                    {listData.slice(0, 3).map(item => (
                        <div
                            key={item.id}
                            className={`status-dot ${getStatusType(item.status)}`}
                        />
                    ))}
                    {listData.length > 3 && <span className="more-dots">...</span>}
                </div>
            </div>
        );
    };



    // Handle Manual Booking Submission
    const handleManualBooking = async (values) => {
        setActionLoading(true);
        try {
            const selectedService = services.find(s => s.id === values.serviceId);

            // 1. Create Booking Record
            const bookingData = {
                userId: 'admin_created', // Placeholder or real user ID if selected
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phone: values.phone,
                preferredDate: values.date.toDate(),
                preferredTime: moment(values.date.format('YYYY-MM-DD') + ' ' + values.time, 'YYYY-MM-DD HH:mm').toDate(),
                selectedServices: selectedService ? [selectedService] : [],
                sessionType: 'video', // Default
                status: 'confirmed', // Admin bookings are auto-confirmed usually
                confirmationNumber: 'ADM-' + Math.floor(Math.random() * 100000),
                notes: values.notes,
                termsAccepted: true
            };

            const newBooking = await bookingsDB.add(bookingData);

            // 2. Mark Slot as Booked
            await timeSlotsDB.bookSlot(values.date.format('YYYY-MM-DD'), values.time, {
                bookingId: newBooking.id,
                patientName: `${values.firstName} ${values.lastName}`,
                patientEmail: values.email,
                serviceType: selectedService?.title,
                notes: 'Manual Admin Booking'
            });

            message.success('Appointment booked successfully!');
            setManualBookingVisible(false);
            bookingForm.resetFields();
            loadBookings(); // Refresh main calendar
            if (dayModalVisible) fetchSlotsForDay(selectedDate); // Refresh day view if open

        } catch (error) {
            console.error('Booking error:', error);
            message.error('Failed to create booking: ' + error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'orange',
            payment_approved: 'gold',
            confirmed: 'blue',
            completed: 'green',
            cancelled: 'red',
            payment_rejected: 'red',
            // Slot statuses
            available: 'blue',
            blocked: 'red',
            booked: 'gold'
        };
        return colors[status] || 'default';
    };

    return (
        <div className={`admin-calendar-container ${isDark ? 'dark' : ''}`}>

            {/* Header Actions */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div className="view-toggle" style={{ background: '#f0f0f0', padding: '4px', borderRadius: '8px', display: 'flex' }}>
                    <Button
                        type={viewMode === 'month' ? 'primary' : 'text'}
                        icon={<CalendarOutlined />}
                        onClick={() => setViewMode('month')}
                        style={{ borderRadius: '6px', boxShadow: viewMode === 'month' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none' }}
                    >
                        Month
                    </Button>
                    <Button
                        type={viewMode === 'week' ? 'primary' : 'text'}
                        icon={<AppstoreOutlined />}
                        onClick={() => setViewMode('week')}
                        style={{ borderRadius: '6px', boxShadow: viewMode === 'week' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none' }}
                    >
                        Week
                    </Button>
                </div>

                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                        bookingForm.setFieldsValue({ date: moment() });
                        setManualBookingVisible(true);
                    }}
                    size="large"
                    shape="round"
                >
                    New Booking
                </Button>
            </div>

            <Row gutter={[16, 16]} className="stats-header-row">
                <Col xs={24} sm={8}>
                    <Card size="small" className="stat-card glass-morphism total">
                        <Statistic
                            title="Monthly Bookings"
                            value={stats.monthTotal}
                            prefix={<CalendarOutlined className="stat-icon" />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card size="small" className="stat-card glass-morphism confirmed">
                        <Statistic
                            title="Confirmed"
                            value={stats.monthConfirmed}
                            prefix={<CheckCircleOutlined className="stat-icon" />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card size="small" className="stat-card glass-morphism pending">
                        <Statistic
                            title="Pending Approval"
                            value={stats.monthPending}
                            prefix={<ClockCircleOutlined className="stat-icon" />}
                        />
                    </Card>
                </Col>
            </Row>

            <Card className="calendar-card" bodyStyle={{ padding: viewMode === 'week' ? '0' : '24px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" tip="Loading calendar data..." />
                    </div>
                ) : (
                    viewMode === 'month' ? (
                        <Calendar
                            dateCellRender={dateCellRender}
                            onSelect={(date) => {
                                handleSelectDay(date);
                                // If switching to week view is desired on click, uncomment:
                                // setViewMode('week');
                            }}
                        />
                    ) : (
                        <WeekSlotGrid
                            bookings={bookings} // Pass bookings to Week View
                            startDate={selectedDate}
                            onDateChange={(date) => setSelectedDate(date)}
                            onSlotAction={(date, time, slot) => {
                                // For now, if slot exists, select it/show details
                                // If slot doesn't exist, maybe offer to create?

                                // To maintain consistency, let's open the day modal for that day
                                // or if it's available, maybe toggle it?
                                // Let's open the day modal for granular control for now
                                // as WeekGrid is high level status.
                                setSelectedDate(moment(date));
                                setDayModalVisible(true);
                            }}
                        />
                    )
                )}
            </Card>

            {/* Day Management Modal with Tabs */}
            <Modal
                title={null}
                open={dayModalVisible}
                onCancel={() => setDayModalVisible(false)}
                footer={null}
                width={800}
                className="day-management-modal"
                centered
                destroyOnClose
            >
                <div className="modal-header-custom" style={{ padding: '20px 24px 0', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            background: '#e6f7ff',
                            padding: '10px',
                            borderRadius: '12px',
                            color: '#1890ff'
                        }}>
                            <CalendarOutlined style={{ fontSize: '24px' }} />
                        </div>
                        <div>
                            <Title level={4} style={{ margin: 0 }}>{selectedDate.format('dddd, MMMM DD')}</Title>
                            <Text type="secondary">{selectedDate.format('YYYY')}</Text>
                        </div>
                    </div>
                </div>

                <Tabs defaultActiveKey="bookings" type="card" style={{ padding: '0 24px 24px' }}>

                    {/* Tab 1: Appointments List */}
                    <TabPane tab="Appointments" key="bookings">
                        <div className="quick-actions-bar" style={{ marginBottom: '16px' }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setDayModalVisible(false);
                                    bookingForm.setFieldsValue({ date: selectedDate });
                                    setManualBookingVisible(true);
                                }}
                            >
                                Add Appointment
                            </Button>
                        </div>

                        <div className="day-bookings-list">
                            {getListData(selectedDate).length > 0 ? (
                                getListData(selectedDate).sort((a, b) => {
                                    const timeA = a.preferredTime?.toDate ? a.preferredTime.toDate() : a.preferredTime;
                                    const timeB = b.preferredTime?.toDate ? b.preferredTime.toDate() : b.preferredTime;
                                    return moment(timeA).diff(moment(timeB));
                                }).map(booking => (
                                    <div
                                        key={booking.id}
                                        className={`booking-card-item ${booking.status}`}
                                        onClick={() => {
                                            setSelectedBooking(booking);
                                            setBookingDetailVisible(true);
                                        }}
                                    >
                                        <div className="booking-time">
                                            <ClockCircleOutlined />
                                            <span>{booking.preferredTime ? moment(booking.preferredTime.toDate ? booking.preferredTime.toDate() : booking.preferredTime).format('HH:mm') : '--:--'}</span>
                                        </div>
                                        <div className="booking-info">
                                            <div className="patient-name">{booking.firstName} {booking.lastName}</div>
                                            <div className="service-tag">{booking.selectedServices?.[0]?.title || 'Consultation'}</div>
                                        </div>
                                        <div className="booking-status">
                                            <Tag color={getStatusColor(booking.status)} className="status-tag">
                                                {booking.status?.replace('_', ' ').toUpperCase()}
                                            </Tag>
                                            <Button type="text" icon={<EyeOutlined />} className="details-btn" />
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <Empty description="No appointments scheduled for this day" />
                            )}
                        </div>
                    </TabPane>

                    {/* Tab 2: Slot Management */}
                    <TabPane tab="Time Slot Management" key="slots">
                        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Text strong>Manage availability for this specific date.</Text>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Add, remove, or block specific time slots.
                                </Text>
                            </div>
                            <Space>
                                <Button
                                    icon={<ReloadOutlined />}
                                    size="small"
                                    onClick={() => setCreateSlotsModalVisible(true)}
                                >
                                    Create Slots
                                </Button>
                                {slots.some(s => s.status === 'available') ? (
                                    <Button
                                        danger
                                        size="small"
                                        icon={<StopOutlined />}
                                        onClick={() => handleDisableAllSlots('blocked')}
                                    >
                                        Block All
                                    </Button>
                                ) : (
                                    <Button
                                        type="primary"
                                        ghost
                                        size="small"
                                        icon={<CheckCircleOutlined />}
                                        onClick={() => handleDisableAllSlots('available')}
                                    >
                                        Unblock All
                                    </Button>
                                )}
                            </Space>
                        </div>

                        {loading ? <Spin /> : (
                            <div className="slots-grid">
                                {slots.map(slot => (
                                    <div
                                        key={slot.id || slot.time}
                                        className={`slot-item ${slot.status}`}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <Text strong className="slot-time-text" style={{ color: 'inherit' }}>{slot.time}</Text>

                                            {slot.status !== 'booked' && (
                                                <Popconfirm
                                                    title="Delete this slot?"
                                                    onConfirm={() => handleDeleteSlot(slot)}
                                                    okText="Yes"
                                                    cancelText="No"
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        className="delete-slot-btn"
                                                        style={{ color: 'inherit', opacity: 0.7 }}
                                                    />
                                                </Popconfirm>
                                            )}
                                        </div>

                                        <div style={{ marginTop: '8px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                                            {slot.status === 'booked' ? (
                                                <Tag color="gold" style={{ margin: 0 }}>BOOKED</Tag>
                                            ) : (
                                                <Tooltip title={slot.status === 'available' ? 'Click to Block' : 'Click to Enable'}>
                                                    <Switch
                                                        size="small"
                                                        checked={slot.status === 'available'}
                                                        checkedChildren={<CheckCircleOutlined />}
                                                        unCheckedChildren={<StopOutlined />}
                                                        onChange={() => handleToggleSlot(slot)}
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                {/* Add Slot Button */}
                                <div
                                    className="slot-item add-slot-btn"
                                    onClick={() => setAddSlotModalVisible(true)}
                                    style={{
                                        border: '1px dashed #d9d9d9',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '80px'
                                    }}
                                >
                                    <PlusOutlined style={{ fontSize: '20px', marginBottom: '8px', color: '#1890ff' }} />
                                    <Text type="secondary">Add Slot</Text>
                                </div>
                            </div>
                        )}
                    </TabPane>
                </Tabs>
            </Modal>

            {/* Add Slot Modal */}
            <Modal
                title="Add New Time Slot"
                open={addSlotModalVisible}
                onCancel={() => setAddSlotModalVisible(false)}
                onOk={handleAddSlot}
                confirmLoading={actionLoading}
                width={300}
                destroyOnClose
            >
                <div style={{ padding: '20px 0', textAlign: 'center' }}>
                    <Text style={{ display: 'block', marginBottom: '8px' }}>Select Time:</Text>
                    <TimePicker
                        format="HH:mm"
                        minuteStep={5}
                        onChange={(time) => setNewSlotTime(time)}
                        value={newSlotTime}
                        style={{ width: '100%' }}
                    />
                </div>
            </Modal>

            {/* Manual Booking Modal */}
            <Modal
                title="Create New Booking"
                open={manualBookingVisible}
                onCancel={() => setManualBookingVisible(false)}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={bookingForm}
                    layout="vertical"
                    onFinish={handleManualBooking}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                                <Input placeholder="John" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label="Last Name" rules={[{ required: true }]}>
                                <Input placeholder="Doe" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                        <Input placeholder="email@example.com" />
                    </Form.Item>

                    <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                        <Input placeholder="+91 9876543210" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
                                <DatePicker
                                    style={{ width: '100%' }}
                                    onChange={(date) => {
                                        if (date) {
                                            setSelectedDate(date); // Update context for fetching slots
                                            fetchSlotsForDay(date); // Fetch slots for new date
                                        }
                                    }}
                                    disabledDate={(current) => current && current < moment().startOf('day')}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="time" label="Time Slot" rules={[{ required: true }]}>
                                <Select placeholder="Select Time">
                                    {slots
                                        .filter(s => s.status === 'available')
                                        .map(s => (
                                            <Option key={s.time} value={s.time}>{s.time}</Option>
                                        ))
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="serviceId" label="Service" rules={[{ required: true }]}>
                        <Select placeholder="Select Service">
                            {services.map(s => (
                                <Option key={s.id} value={s.id}>{s.title} ({s.duration})</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="notes" label="Admin Notes">
                        <Input.TextArea placeholder="Any special notes..." rows={2} />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={actionLoading}>
                            Confirm Booking
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Booking Details Modal (Existing) */}
            <Modal
                title={
                    <div className="modal-header-custom">
                        <UserOutlined className="header-icon" />
                        <div className="header-text">
                            <Title level={4} style={{ margin: 0 }}>Appointment Details</Title>
                            <Tag color={getStatusColor(selectedBooking?.status)} className="status-header-tag">
                                {selectedBooking?.status?.replace('_', ' ').toUpperCase()}
                            </Tag>
                        </div>
                    </div>
                }
                open={bookingDetailVisible}
                onCancel={() => setBookingDetailVisible(false)}
                footer={[
                    <Button key="close" shape="round" size="large" onClick={() => setBookingDetailVisible(false)}>
                        Close
                    </Button>
                ]}
                width={700}
                className="booking-details-modal"
                centered
                zIndex={1100}
            >
                {selectedBooking && (
                    <div className="details-container">
                        <Card className="patient-main-card">
                            <Row gutter={16} align="middle">
                                <Col>
                                    <div className="avatar-placeholder">
                                        {selectedBooking.firstName?.[0]}{selectedBooking.lastName?.[0]}
                                    </div>
                                </Col>
                                <Col flex="auto">
                                    <Title level={3} style={{ margin: 0 }}>{selectedBooking.firstName} {selectedBooking.lastName}</Title>
                                    <Space split={<div className="divider-dot" />}>
                                        <Text type="secondary">{selectedBooking.email}</Text>
                                        <Text type="secondary">{selectedBooking.phone}</Text>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>

                        <div className="details-grid">
                            <div className="detail-item">
                                <CalendarOutlined className="item-icon" />
                                <div className="item-content">
                                    <Text type="secondary" className="label">Date</Text>
                                    <Text strong className="value">
                                        {moment(selectedBooking.preferredDate?.toDate ? selectedBooking.preferredDate.toDate() : selectedBooking.preferredDate).format('MMMM DD, YYYY')}
                                    </Text>
                                </div>
                            </div>
                            <div className="detail-item">
                                <ClockCircleOutlined className="item-icon" />
                                <div className="item-content">
                                    <Text type="secondary" className="label">Time</Text>
                                    <Text strong className="value">
                                        {moment(selectedBooking.preferredTime?.toDate ? selectedBooking.preferredTime.toDate() : selectedBooking.preferredTime).format('HH:mm')}
                                    </Text>
                                </div>
                            </div>
                            <div className="detail-item full-width">
                                <Badge status="processing" className="item-icon-small" />
                                <div className="item-content">
                                    <Text type="secondary" className="label">Service</Text>
                                    <Text className="value">
                                        {selectedBooking.selectedServices?.map(s => s.title).join(', ') || 'Consultation'}
                                    </Text>
                                </div>
                            </div>
                            <div className="detail-item full-width">
                                <div className="item-content">
                                    <Text type="secondary" className="label">Patient Concerns</Text>
                                    <div className="concerns-box">
                                        {selectedBooking.currentConcerns || 'No specific concerns noted by the patient.'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Create Slots Range Modal */}
            <Modal
                title="Generate Multiple Slots"
                open={createSlotsModalVisible}
                onCancel={() => setCreateSlotsModalVisible(false)}
                onOk={handleCreateSlots}
                confirmLoading={actionLoading}
                okText="Generate"
                width={400}
                destroyOnClose
            >
                <Form layout="vertical" style={{ marginTop: 16 }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Start Time">
                                <TimePicker
                                    format="HH:mm"
                                    minuteStep={15}
                                    value={createStartTime}
                                    onChange={setCreateStartTime}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="End Time">
                                <TimePicker
                                    format="HH:mm"
                                    minuteStep={15}
                                    value={createEndTime}
                                    onChange={setCreateEndTime}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item label="Slot Duration (minutes)">
                        <Select value={slotDuration} onChange={setSlotDuration}>
                            <Option value={15}>15 Minutes</Option>
                            <Option value={30}>30 Minutes</Option>
                            <Option value={45}>45 Minutes</Option>
                            <Option value={60}>60 Minutes</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item>
                        <Checkbox checked={preserveSlots} onChange={(e) => setPreserveSlots(e.target.checked)}>
                            Preserve existing slots
                        </Checkbox>
                    </Form.Item>

                    <Alert
                        type={preserveSlots ? "info" : "warning"}
                        showIcon
                        message={preserveSlots ? "Note" : "Warning"}
                        description={preserveSlots
                            ? "New slots will be added to the schedule. Existing slots will be kept."
                            : "This will replace all available/blocked slots for the day. Existing bookings will be preserved."}
                        style={{ fontSize: '12px' }}
                    />
                </Form>
            </Modal>
        </div>
    );
};

export default AdminCalendarView;
